import React, { Fragment } from 'react';
import { connect } from 'react-redux';
import styled from 'styled-components';
import Menu from '@mui/material/Menu/Menu';
import MenuItem from '@mui/material/MenuItem';
import Icon from '@mui/material/Icon';
import moment from 'moment';
import VaultButton from '../../Shared/VaultButton';
import {
  IDispatchProps,
  IProps,
} from './interfaces';
import { StreamStatus, UiSection } from '../../../enums';
import { Routes } from '../../../constants/routes';
import {
  openAssetModal, setDeleteConfirmDialogOpen, setRecoverConfirmDialogOpen, setStopConfirmDialogOpen,
  setTerminateConfirmDialogOpen,
} from '../../../state/liveEvents/liveAssetModal';
import { editAssetMetadata } from '../../AssetDetails/api/editAssetMetadata';
import { recoverLiveStreamAvailable } from '../../../api/vault/assets/api';
import toast from '../../../utils/toast';
import { Colors } from '../../../constants';

const StyledButton = styled(VaultButton)`
  border-radius: 50%;
  &:hover {
    background-color: ${Colors.BLACK_05};
  }
  &.inJobsTab:hover {
    background-color: ${Colors.WHITE_90};
  }
`;

// Processing is genuinely mid-flight and must not be touched. Restarting used to
// be here too, which meant an asset left stuck in it - a restart that failed
// part-way - offered no action at all and its recorded footage could not be
// salvaged from the UI.
// SourceEnded sits here with Processing for the same reason: the event is already
// being stopped and processed, so offering a restart or a recovery would race the
// termination that is underway.
const disabledMenuStatuses = [StreamStatus.Processing.toString(), StreamStatus.SourceEnded.toString()];

const LiveEventMenu: React.FC<IProps> = (props: IProps) => {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [recoverable, setRecoverable] = React.useState<boolean>(false);
  const {
    asset, inJobsTab, openAssetModal,
    setDeleteConfirmDialogOpen, setStopConfirmDialogOpen, setTerminateConfirmDialogOpen,
    setRecoverConfirmDialogOpen,
  } = props;

  // Statuses where footage could exist at all; the live check decides the rest.
  const canEverRecover = [StreamStatus.Error.toString(), StreamStatus.AwaitingSource.toString(), StreamStatus.Restarting.toString()]
    .includes(asset._source.streamStatus);

  // The events list refreshes in the background, so an asset can change status
  // while this component is mounted - and a stale "true" would put Recover video
  // back in front of someone for an event that no longer has anything to recover.
  // Dropping it on any change means the worst case is a missing option until the
  // menu is opened again, which is the direction this feature errs in anyway.
  React.useEffect(() => {
    setRecoverable(false);
  }, [asset._id, asset._source.streamStatus]);

  const liveMenuID: string = `${UiSection.Header}.Menu.LiveEvents`;
  const liveButtonID: string = `${UiSection.Header}.Button.LiveEvents`;

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
    event.stopPropagation();

    // Asked when the menu opens rather than per row: this is a live check against
    // the CDN, and the events list can be a hundred rows deep. Hidden until it
    // comes back true, so a recovery is never offered for an event with nothing to
    // recover - which is the common case for a feed that never published.
    if (canEverRecover) {
      setRecoverable(false);
      recoverLiveStreamAvailable(asset._id)
        .then((response) => setRecoverable(!!response?.data?.data?.recoverable))
        .catch(() => setRecoverable(false));
    }
  };

  const handleDeleteOption = (e: React.MouseEvent) => {
    setDeleteConfirmDialogOpen(true, asset);
    setAnchorEl(null);
    e.stopPropagation();
  };

  const handleTerminateOption = (e: React.MouseEvent) => {
    setTerminateConfirmDialogOpen(true, asset);
    setAnchorEl(null);
    e.stopPropagation();
  };

  const handleStartNowOption = (e: React.MouseEvent) => {
    asset._source.start = moment().toISOString();
    editAssetMetadata({ ...asset._source, _id: asset._id });
    toast('Stream will start as soon as possible', {
      type: toast.TYPE.WARNING,
    });
    setAnchorEl(null);
    e.stopPropagation();
  };

  const handleStopOption = (e: React.MouseEvent) => {
    setStopConfirmDialogOpen(true, asset);
    setAnchorEl(null);
    e.stopPropagation();
  };

  const handleEditOption = (e: React.MouseEvent) => {
    openAssetModal(false, false, asset);
    setAnchorEl(null);
    e.stopPropagation();
  };

  const handleExtendOption = (e: React.MouseEvent) => {
    openAssetModal(true, false, asset);
    setAnchorEl(null);
    e.stopPropagation();
  };

  const handleOpenAssetOption = async (assetId: string) => {
    window.open(`${Routes.HOME}?assetId=${assetId}`);
  };

  const handleRecoverOption = (e: React.MouseEvent) => {
    setRecoverConfirmDialogOpen(true, asset);
    setAnchorEl(null);
    e.stopPropagation();
  };

  const handleRestartOption = (e: React.MouseEvent) => {
    openAssetModal(false, true, asset, [
      { name: 'start', value: moment().toISOString() },
      { name: 'stop', value: moment().add(3, 'hours').toISOString() },
    ]);
    setAnchorEl(null);
    e.stopPropagation();
  };

  const handleClose = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(null);
    event.stopPropagation();
  };

  const getMenu = () => {
    if (asset._source.streamStatus === StreamStatus.Scheduled) {
      return (
        <Menu id={liveMenuID} anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleClose}>
          <MenuItem
            onClick={() => handleOpenAssetOption(asset._id)}
            data-auto="OpenBtn"
          >
            Open
          </MenuItem>
          <MenuItem
            onClick={handleEditOption}
            data-auto="LiveEventEditBtn"
          >
            Edit
          </MenuItem>
          <MenuItem
            onClick={handleDeleteOption}
            data-auto="LiveEventDeleteBtn"
          >
            Delete
          </MenuItem>
          <MenuItem
            onClick={handleStartNowOption}
            data-auto="LiveEventStartNowBtn"
          >
            Start Now
          </MenuItem>
        </Menu>
      );
    } if (asset._source.streamStatus === StreamStatus.Live) {
      return (
        <Menu id={liveMenuID} anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleClose}>
          <MenuItem
            onClick={() => handleOpenAssetOption(asset._id)}
            data-auto="OpenBtn"
          >
            Open
          </MenuItem>
          <MenuItem
            onClick={handleExtendOption}
            data-auto="LiveEventExtendBtn"
          >
            Edit
          </MenuItem>
          <MenuItem
            onClick={handleStopOption}
            data-auto="LiveEventStopBtn"
          >
            Stop
          </MenuItem>
          <MenuItem
            onClick={handleRestartOption}
            data-auto="LiveEventRestartBtn"
          >
            Restart
          </MenuItem>
          <MenuItem
            onClick={handleTerminateOption}
            data-auto="LiveEventTerminateBtn"
          >
            Terminate
          </MenuItem>
        </Menu>
      );
    } if (asset._source.streamStatus === StreamStatus.Initializing) {
      // Allow terminating a stream stuck in Initializing (tears down the
      // stream host and deletes the event)
      return (
        <Menu id={liveMenuID} anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleClose}>
          <MenuItem
            onClick={() => handleOpenAssetOption(asset._id)}
            data-auto="OpenBtn"
          >
            Open
          </MenuItem>
          <MenuItem
            onClick={handleTerminateOption}
            data-auto="LiveEventTerminateBtn"
          >
            Terminate
          </MenuItem>
        </Menu>
      );
    } if (disabledMenuStatuses.includes(asset._source.streamStatus)) {
      return (
        <Menu id={liveMenuID} anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleClose}>
          <MenuItem
            onClick={() => handleOpenAssetOption(asset._id)}
            data-auto="OpenBtn"
          >
            Open
          </MenuItem>
        </Menu>
      );
    } if (asset._source.streamStatus === StreamStatus.Error
      || asset._source.streamStatus === StreamStatus.AwaitingSource) {
      // AwaitingSource shares this menu: the same actions apply, though a restart
      // is rarely what you want since the host is already waiting for the feed and
      // will start on its own.
      return (
        <Menu id={liveMenuID} anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleClose}>
          <MenuItem
            onClick={() => handleOpenAssetOption(asset._id)}
            data-auto="OpenBtn"
          >
            Open
          </MenuItem>
          <MenuItem
            onClick={handleRestartOption}
            data-auto="LiveEventRestartBtn"
          >
            Restart
          </MenuItem>
          {recoverable && (
            <MenuItem
              onClick={handleRecoverOption}
              data-auto="LiveEventRecoverBtn"
            >
              Recover video
            </MenuItem>
          )}
          <MenuItem
            onClick={handleStopOption}
            data-auto="LiveEventStopBtn"
          >
            Stop
          </MenuItem>
          <MenuItem
            onClick={handleTerminateOption}
            data-auto="LiveEventTerminateBtn"
          >
            Terminate
          </MenuItem>
        </Menu>
      );
    } if (asset._source.streamStatus === StreamStatus.Restarting) {
      // A restart in flight normally clears itself within a minute. When it does
      // not, this is the way back: recover the footage, or tear the event down.
      return (
        <Menu id={liveMenuID} anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleClose}>
          <MenuItem
            onClick={() => handleOpenAssetOption(asset._id)}
            data-auto="OpenBtn"
          >
            Open
          </MenuItem>
          {recoverable && (
            <MenuItem
              onClick={handleRecoverOption}
              data-auto="LiveEventRecoverBtn"
            >
              Recover video
            </MenuItem>
          )}
          <MenuItem
            onClick={handleTerminateOption}
            data-auto="LiveEventTerminateBtn"
          >
            Terminate
          </MenuItem>
        </Menu>
      );
    }
    return (
      <Menu id={liveMenuID} anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleClose}>
        <MenuItem
          onClick={() => handleOpenAssetOption(asset._id)}
          data-auto="OpenBtn"
        >
          Open
        </MenuItem>
        <MenuItem onClick={handleDeleteOption}>Delete</MenuItem>
      </Menu>
    );
  };

  return (
    <>
      <StyledButton
        className={inJobsTab ? 'inJobsTab' : ''}
        id={liveButtonID}
        aria-owns={anchorEl ? 'simple-menu' : undefined}
        variant="transparentOnWhite"
        aria-haspopup="true"
        onClick={handleClick}
      >
        <Icon>more_vert</Icon>
      </StyledButton>
      {getMenu()}
    </>
  );
};

const mapDispatchToProps: IDispatchProps = {
  openAssetModal,
  setDeleteConfirmDialogOpen,
  setStopConfirmDialogOpen,
  setTerminateConfirmDialogOpen,
  setRecoverConfirmDialogOpen,
};

export default connect(
  null,
  mapDispatchToProps,
)(LiveEventMenu);
